import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { Box, Stack, CircularProgress, Tooltip } from '@mui/material'
import SyncProblemRoundedIcon from '@mui/icons-material/SyncProblemRounded'
import PublishedWithChangesRoundedIcon from '@mui/icons-material/PublishedWithChangesRounded'
import { TWheatCode } from 'types'
import { Card, DeleteIcon, EditIcon, Table } from 'components'
import { useDeliveriesService, useWheatCodesService } from 'services'
import { useDeleteModal } from 'components/dialogs'
import { CrudAction, Entity, usePermissions } from 'utils/hooks'
import { successToast } from 'utils/toastService'
import {
  addWheatCodeWithTaskIds,
  REQUESTSTATUS,
  TRecalculateTask,
} from 'store/slices/recalculateBinSlice'
import { TableContainer } from 'components/Table/table.style'
import { TableActionConfig } from '../../../../../../components/Table/types'
import { ListPagination } from '../../../../../../services/types'
import {
  TFiltersState,
  TSortState,
} from '../../../../../../components/Table/TableFilter/types'
import useEditModal from '../../dialogs/useEditModal'
import useRecalculateModal from '../../dialogs/useRecalculateModal'
import { WheatCodeRowDetails } from './WheatCodeRowDetails'
import { wheatCodesTableColumns } from './columns'
import * as sxStyles from './WheatCodeList.styles'
import { useRoles } from '../../../../../../utils/hooks/useRoles'
import { useSystemSettings } from '../../../../../../utils/hooks/useSystemSettings'

type WheatCodeListProps = {
  modifiedFields: Record<number, number>
  handleCostChange: (id: number, cost: number) => void
  wheatCodes: TWheatCode[]
  pagination?: ListPagination
  isWheatCodesLoading: boolean
  formatFiltersSorts: (filters: TFiltersState, sort: TSortState) => void
  setCurrentPage: (page: number) => void
  setRowsPerPage: (rowsPerPage: number) => void
}

export const WheatCodeList = ({
  modifiedFields,
  handleCostChange,
  wheatCodes,
  pagination,
  isWheatCodesLoading,
  formatFiltersSorts,
  setCurrentPage,
  setRowsPerPage,
}: WheatCodeListProps) => {
  const { checkPermission } = usePermissions()
  const { deleteWheatCode, updateWheatCode, getWheatCodeStatuses } =
    useWheatCodesService()
  const { recalculateDeliveriesAccordingToNewTargetBin } =
    useDeliveriesService()
  const queryClient = useQueryClient()
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null)
  const [editingItem, setEditingItem] = useState<TWheatCode | null>(null)
  const [recalculateWheatCodeId, setRecalculateWheatCodeId] = useState<
    number | null
  >(null)
  const dispatch = useDispatch()

  const { isSuperAdmin, isSuperUser } = useRoles()
  const { isEnabled: displayTargetBin } = useSystemSettings(
    'SHOW_WHEAT_TARGET_BIN'
  )

  const onPaginationChange = (page: number, perPage: number) => {
    setCurrentPage(page)
    setRowsPerPage(perPage)
  }

  const { mutate: deleteWheatCodeMutation } = useMutation(deleteWheatCode, {
    onSuccess: (data) => {
      setDeleteItemId(null)
      void queryClient.invalidateQueries(['wheat_codes_list'])
      successToast(
        data?.detail ? data.detail : 'Wheat Code deleted successfully'
      )
    },
    onError: (error: { detail?: string }) => {
      toast.error(error?.detail ? error.detail : 'Wheat Code cannot be deleted')
    },
  })

  const { mutateAsync: updateWheatCodeMutation } = useMutation(
    updateWheatCode,
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(['wheat_codes_list'])
        successToast('Wheat code updated')
      },
    }
  )

  const { mutateAsync: recalculateDeliveriesMutation } = useMutation(
    recalculateDeliveriesAccordingToNewTargetBin,
    {
      onSuccess: () => {
        successToast(
          'Recalculation of deliveries according to new target bin in wheat code has succeeded'
        )
      },
    }
  )

  const [deleteModal, toggleDeleteModal] = useDeleteModal({
    children: 'Are you sure you want to remove this wheat code?',
    onConfirm: () => {
      if (deleteItemId !== null) {
        void deleteWheatCodeMutation(deleteItemId)
      }
    },
  })

  const [editModal, toggleEditModal] = useEditModal({
    initialData: editingItem,
    mode: 'edit',
    onConfirm: async (data: TWheatCode) => {
      try {
        await updateWheatCodeMutation(data)
        setEditingItem(null)
        toggleEditModal()
      } catch (error) {
        toast.error(
          error?.detail ? error.detail : 'Failed to update Wheat Code'
        )
      }
    },
  })

  const [calculateModal, togglecalculateModal] = useRecalculateModal({
    wheatCodeId: recalculateWheatCodeId,
    onConfirm: async (wcId: number) => {
      try {
        const res = await recalculateDeliveriesMutation(wcId)
        setEditingItem(null)
        togglecalculateModal()
        if (res) {
          const newWheatCodeToCalculate: TRecalculateTask = {
            wheatCodeId: wcId,
            taskId: res?.task_id,
            status: REQUESTSTATUS.PENDING,
          }
          dispatch(addWheatCodeWithTaskIds(newWheatCodeToCalculate))
        }
      } catch (error) {
        console.error('Failed to recalculate deliveries')
      }
    },
  })

  const { data: wheatCodeStatuses, isLoading: isStatusesLoading } = useQuery({
    queryKey: ['wheat_codes_statuses'],
    queryFn: () => getWheatCodeStatuses(),
  })

  const statusesOptions = wheatCodeStatuses?.results?.map((item) => ({
    label: item?.name,
    value: item?.id?.toString(),
  }))

  const actions: TableActionConfig<TWheatCode>[] = [
    (isSuperAdmin || isSuperUser) && displayTargetBin
      ? {
          name: (row: TWheatCode) =>
            row.is_recalculate_needed ? 'recalculate' : 'calculated',
          icon: (row: TWheatCode) =>
            row.is_recalculate_needed ? (
              <Tooltip
                placement="top"
                title={
                  'The target bin of the wheat code has changed. A recalculation is required to accommodate it retroactively'
                }
              >
                <SyncProblemRoundedIcon
                  color="primary"
                  sx={sxStyles.recalculateBtn}
                />
              </Tooltip>
            ) : (
              <Tooltip
                placement="top"
                title={
                  'There is no change in the Target bin of the wheat code. No recalculation required'
                }
              >
                <PublishedWithChangesRoundedIcon sx={sxStyles.calculatedBtn} />
              </Tooltip>
            ),
          handler: (row: TWheatCode) => {
            if (row.is_recalculate_needed) {
              setRecalculateWheatCodeId(row.id)
              togglecalculateModal()
            }
          },
        }
      : null,
    {
      name: 'edit',
      icon: <EditIcon size={16} />,
      handler: (row: TWheatCode) => {
        setEditingItem(row)
        toggleEditModal()
      },
    },
    {
      name: 'delete',
      icon: <DeleteIcon size={20} />,
      handler: (row: TWheatCode) => {
        if (!row?.id) return
        setDeleteItemId(row.id)
        toggleDeleteModal()
      },
      permission: {
        entity: Entity.WHEAT_CODE,
        action: CrudAction.DELETE,
      },
    },
  ].filter(Boolean)

  const renderCost = checkPermission(Entity.WHEAT_CODE, 'cost_read')

  const updatedColumns = useMemo(() => {
    if (!statusesOptions?.length) {
      return []
    }

    return wheatCodesTableColumns(
      actions,
      modifiedFields,
      handleCostChange,
      statusesOptions
    ).filter((column) => (column.id === 'cost' ? renderCost : true))
  }, [modifiedFields, handleCostChange, renderCost, statusesOptions, actions])

  // Don't render the table until columns are ready
  const isReady = !isStatusesLoading && updatedColumns.length > 0

  return (
    <Card sx={sxStyles.container}>
      <Box sx={sxStyles.listWrapper}>
        {isReady ? (
          <TableContainer>
            <Table
              columns={updatedColumns}
              data={wheatCodes}
              showPagination={true}
              total={pagination?.total || 0}
              isLoading={isWheatCodesLoading}
              handleGlobalFilter={(filter, sort) => {
                formatFiltersSorts(filter, sort)
              }}
              handleGlobalSort={(sort, filter) => {
                formatFiltersSorts(filter, sort)
              }}
              onPaginationChange={onPaginationChange}
              renderExpandableRow={(row) => (
                <Stack sx={sxStyles.renderDetailsContainer}>
                  {row?.attributes.map((detail, index) => (
                    <WheatCodeRowDetails key={index} {...detail} />
                  ))}
                </Stack>
              )}
              displayShowAll
            />
          </TableContainer>
        ) : (
          <Box sx={sxStyles.circularProgressIcon}>
            <CircularProgress />
          </Box>
        )}
      </Box>
      {deleteModal}
      {editModal}
      {calculateModal}
    </Card>
  )
}
